import Const from '../config/const';

/**
 * Where the user came from — the `?application=<key>` handoff param — has to
 * survive every hop this frontend can take before the handoff completes: a
 * stale-token bounce through /home, a 401 reload, a 2FA step, a
 * forgot-password detour, a manual refresh. Relying on the query string alone
 * loses it the moment any one redirect forgets to carry it, and the user then
 * lands on the portal instead of back in their app.
 *
 * So the param is also parked in sessionStorage (per-tab, gone when the tab
 * closes) and read back from there whenever the URL no longer carries it.
 */

const STORAGE_KEY = 'sso_pending_application';

/**
 * Const keys that are NOT client-app login URLs. Every redirect does a bare
 * `Const[application]` lookup, so `?application=apiUrl` would otherwise send
 * the user at the API host.
 *
 * Deliberately a denylist, not an allowlist of app names: an allowlist silently
 * drops the handoff for any app someone forgets to add to it, which is exactly
 * the failure this whole module exists to prevent. Adding a new app URL to
 * config/const.ts is enough to make its handoff work.
 */
const NON_APPLICATION_KEYS: readonly string[] = ['apiUrl', 'linentechLogout', 'linendeskLogout'];

/**
 * Resolves an incoming `?application=` value to the Const key that holds its
 * URL. Matches case-insensitively because the two casings are not interchangeable
 * elsewhere: the Const key for LinenIQ is `linenIq`, while the name the API and
 * the `application` DB table use is lowercase `lineniq` (see AllProduct.tsx,
 * which lowercases before calling /product/get-token). Returns null when there
 * is no URL configured to send the user to — better to keep them on the portal
 * than to navigate to the string "undefined?ott=…".
 */
export const resolveApplicationConstKey = (application?: string | null): string | null => {
  if (!application) return null;
  const isUsableUrl = (key: string) =>
    !NON_APPLICATION_KEYS.includes(key) && /^https?:\/\//i.test(String(Const[key] ?? ''));
  if (isUsableUrl(application)) return application;
  const lowered = application.toLowerCase();
  const match = Object.keys(Const).find((key) => key.toLowerCase() === lowered);
  return match && isUsableUrl(match) ? match : null;
};

/** A handoff target we can actually reach. */
export const isKnownApplication = (application?: string | null): boolean =>
  resolveApplicationConstKey(application) !== null;

const readStored = (): string | null => {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // Private-mode / storage-disabled browsers: fall back to the URL only.
    return null;
  }
};

/** Parks the app for later hops. Unknown values are ignored, not stored. */
export const rememberApplication = (application?: string | null): void => {
  if (!isKnownApplication(application)) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, application as string);
  } catch {
    // Nothing to fall back to — the URL param still works for this hop.
  }
};

/**
 * The app this user should end up in, URL first (an explicit param always wins
 * over a remembered one) then the remembered value.
 */
export const getPendingApplication = (): string | null => {
  const fromUrl = new URLSearchParams(window.location.search).get('application');
  if (isKnownApplication(fromUrl)) return fromUrl;
  const stored = readStored();
  return isKnownApplication(stored) ? stored : null;
};

/**
 * A handoff that failed has to explain itself on the page the user lands on,
 * not the one they are leaving: the toast is raised here but `/home` is a full
 * document load away, which tears the ToastContainer down before it can be
 * read. So the message is parked and shown once on arrival.
 */
const ERROR_KEY = 'sso_handoff_error';

export const rememberHandoffError = (message: string): void => {
  try {
    sessionStorage.setItem(ERROR_KEY, message);
  } catch {
    // No storage: the user lands on the portal without an explanation.
  }
};

/** Single use — a refresh of /home must not replay it. */
export const consumeHandoffError = (): string | null => {
  try {
    const message = sessionStorage.getItem(ERROR_KEY);
    sessionStorage.removeItem(ERROR_KEY);
    return message;
  } catch {
    return null;
  }
};

export const clearPendingApplication = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to clear.
  }
};

/**
 * Records `?application=` from the current URL. Called before React renders so
 * that a route-level redirect can never strip the param before it is captured.
 */
export const captureApplicationFromUrl = (): void => {
  rememberApplication(new URLSearchParams(window.location.search).get('application'));
};

/**
 * `path` with `?application=<pending>` appended when a handoff is in flight.
 * Idempotent — a path that already names an application is returned untouched,
 * so it is safe to wrap a path that already carries the current query string.
 */
export const withApplication = (path: string): string => {
  const queryStart = path.indexOf('?');
  const query = queryStart === -1 ? '' : path.slice(queryStart + 1);
  // Parsed, not substring-matched: a path carrying ?error_message=… must not be
  // mistaken for one that already names an application.
  if (new URLSearchParams(query).has('application')) return path;
  const application = getPendingApplication();
  if (!application) return path;
  // Appends only — never re-serializes the existing query, so other params keep
  // their exact original encoding.
  const separator = queryStart === -1 ? '?' : '&';
  return `${path}${separator}application=${encodeURIComponent(application)}`;
};
